/**
 * CommunityLiveMessageItem — the community-wall tile for a `type = live` message:
 * the broadcaster avatar, a "LIVE" badge, the timestamp, and a bordered
 * "Join Live" / "Live ended" call-to-action. Tapping "Join Live" (only while the
 * session is live) fires `onLiveItemTap(message)`; the page owns the join-confirm
 * sheet + navigation.
 *
 * Flutter source of truth: `messageTypeLive`
 * (`community_message_item.dart:482-558`):
 *  - broadcaster avatar from
 *    `TextUtils.generateUserProfileImageUrl(message.messageUsersInfo.userId)`
 *    (`:504-505`);
 *  - a centered "LIVE" label (`AppStyles.liveStyle`, `:510-511`);
 *  - the message timestamp (`:515`);
 *  - a bordered button whose border is `activeRed` while live / `exclusive` once
 *    ended (`:532-537`) and whose label is `"Join Live"` / `"Live ended"`
 *    (`:544-546`). Tap fires `onLiveItemTap(message)` ONLY when
 *    `broadcastingInfo!.isLive` (`:519-522`) — the ended state is inert.
 *
 * This is a standalone full-width row — unlike TEXT/MEDIA rows it does NOT use
 * the shared reply-preview / reactions scaffold (parity with Flutter's separate
 * `messageTypeLive`).
 *
 * `broadcastingInfo` is read directly off the typed `CommunityChatMessageEntity`
 * (a `MessageBroadcastingInfoEntity` populated by the domain remapper) — the tile
 * consumes a typed domain entity and never invokes a remapper itself.
 * `timestampLabel` is supplied preformatted by the caller (mirrors
 * `CommunityMessageItem`); the tile never formats its own timestamp.
 *
 * Web vs Flutter UI parity (theme/responsive latitude, `presentation.md`):
 *  - tile background → `theme.surface` (Flutter `AppColors.backgroundColorLight`);
 *  - "LIVE" label color → `theme.text.primary` (Flutter `liveStyle` `textColor`);
 *  - the CTA reuses the shared `ExpauseButton` (the community/publish standard)
 *    rather than Flutter's hand-rolled bordered pill: `primary` variant while
 *    live / `secondary` once ended. The Flutter border-color + `letterSpacing`
 *    cosmetics are subsumed by the shared button styling.
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { CachedImage } from '@/presentation/components/common/CachedImage';
import {
  ExpauseButton,
  ButtonVariant,
} from '@/presentation/components/common/controls/ExpauseButton';
import { UserUtils } from '@/domain/utils/userUtils';
import { qaAttr } from '@/presentation/utils/qaAttr';
import type { CommunityChatMessageEntity } from '@/domain/entities/community/communityChatMessageEntity';

export interface CommunityLiveMessageItemProps {
  /** The `type = live` message to render. */
  message: CommunityChatMessageEntity;
  /**
   * Current user's Firebase id — drives the owner guard. The live message is
   * authored by the wall owner (= the broadcaster), so
   * `message.messageUsersInfo.userId === currentUserId` means "this is MY own
   * broadcast" (Flutter `!info.isPeer`, `community_message_item.dart:52-53`).
   */
  currentUserId: string;
  /**
   * Preformatted timestamp (`useDateFormatters().formatDate(createdTimestamp)`),
   * supplied by the caller exactly like `CommunityMessageItem` — the tile never
   * formats its own timestamp.
   */
  timestampLabel: string;
  /**
   * Tap the "Join Live" CTA on an ACTIVE session → the page opens the join
   * confirm sheet + launches the audience live page
   * (`community_screen.dart:700-718`). Inert while the session has ended
   * (`community_message_item.dart:519-522`) OR when the current user is the
   * broadcaster (own-broadcast owner guard).
   */
  onLiveItemTap: (message: CommunityChatMessageEntity) => void;
}

export const CommunityLiveMessageItem: React.FC<CommunityLiveMessageItemProps> = ({
  message,
  currentUserId,
  timestampLabel,
  onLiveItemTap,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();

  // Typed read of the domain entity's `broadcastingInfo` — no remapper call from
  // the UI (the domain layer already populated it as a `MessageBroadcastingInfoEntity`).
  const info = message.broadcastingInfo;
  const isLive = info?.isLive ?? false;

  // Owner guard (finding 1): the broadcaster can open a second web tab on their
  // OWN community wall and see the LIVE tile for their in-progress session.
  // Tapping "Join Live" there would join their own channel as audience. The
  // live message is authored by the wall owner, so this is my own broadcast when
  // `message.messageUsersInfo.userId === currentUserId` (Flutter `!info.isPeer`,
  // `community_message_item.dart:52-53`). Flutter parity: `messageTypeLive`,
  // `community_message_item.dart:517`.
  const isOwnBroadcast = message.messageUsersInfo.userId === currentUserId;
  // Joinable only while live AND not the broadcaster's own session.
  const canJoin = isLive && !isOwnBroadcast;

  // Tap fires only while joinable (parity `community_message_item.dart:519-522`
  // + the owner guard); the ended / own-broadcast states render as a disabled
  // (non-interactive) button.
  const handleJoin = (): void => {
    if (canJoin) {
      onLiveItemTap(message);
    }
  };

  // QA status: `live` (joinable) / `you-are-live` (own active broadcast) /
  // `ended` — so a browser test can assert the owner-guarded state.
  const qaStatus = isLive ? (isOwnBroadcast ? 'you-are-live' : 'live') : 'ended';

  return (
    <div
      {...qaAttr({ id: `community-live-message-${message.id}`, status: qaStatus })}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        boxSizing: 'border-box',
        backgroundColor: theme.surface,
        borderRadius: responsive.borderRadius.base,
        overflow: 'hidden',
        paddingTop: responsive.spacing.lg,
        paddingBottom: responsive.spacing.lg,
      }}
    >
      {/* Broadcaster avatar (`community_message_item.dart:501-506`). */}
      <CachedImage
        src={UserUtils.generateUserProfileImageUrl(message.messageUsersInfo.userId)}
        alt=""
        style={{
          width: responsive.avatar.xlarge,
          height: responsive.avatar.xlarge,
          borderRadius: responsive.borderRadius.round,
          objectFit: 'cover',
          flexShrink: 0,
          marginBottom: responsive.spacing.sm,
        }}
      />

      {/* "LIVE" label (`AppStyles.liveStyle`, `:510-511`). */}
      <span
        style={{
          color: theme.text.primary,
          fontSize: responsive.font.lg,
          fontWeight: responsive.fontWeight.bold,
          textAlign: 'center',
          marginBottom: responsive.spacing['3xl'],
        }}
      >
        {l10n.community.liveStreamingPage.liveLabel}
      </span>

      {/* Timestamp (`liveMessageTimestamp`, `:515`). */}
      <span
        style={{
          color: theme.text.secondary,
          fontSize: responsive.font.xs,
          fontWeight: responsive.fontWeight.regular,
          textAlign: 'center',
          marginBottom: responsive.spacing.xs,
        }}
      >
        {timestampLabel}
      </span>

      {/* "Join Live" / "You are LIVE" / "Live ended" CTA (`:517-553`), reusing
          the shared `ExpauseButton`: `primary` while live (the active join
          action) / `secondary` once ended, and disabled (non-interactive) once
          ended OR on the broadcaster's own active session (owner guard, finding
          1 / parity `:519-522`, `:517`). Label precedence mirrors Flutter
          `:544-546`: `isLive` gates FIRST — an ENDED session always reads
          "Live ended" for every viewer (broadcaster included); only an ACTIVE
          session splits into "You are LIVE" (own broadcast) vs "Join Live"
          (audience). No `marginTop` here — the timestamp above owns the column
          spacing (its `marginBottom: responsive.spacing.xs`). */}
      <ExpauseButton
        {...qaAttr({ id: `community-live-message-button-${message.id}` })}
        variant={isLive ? ButtonVariant.primary : ButtonVariant.secondary}
        disabled={!canJoin}
        onClick={handleJoin}
      >
        {!isLive
          ? l10n.community.liveStreamingPage.liveEnded
          : isOwnBroadcast
            ? l10n.community.liveStreamingPage.youAreLive
            : l10n.community.liveStreamingPage.joinLive}
      </ExpauseButton>
    </div>
  );
};
